import { defineStore } from 'pinia';

// Mirrors ADMIN_ROLE_NAME in the backend's src/oauth/rbac.py: the one role that
// bypasses the permission table instead of holding rows in it. Named once here
// so renaming the role is a one-line change on this side.
export const ADMIN_ROLE = 'Admin';

// The role a project's creator takes on their own project. Seeded in the
// backend's src/common/permissions.py; named here so the board resolves it
// from the role catalogue by a shared constant rather than its own literal.
export const MANAGER_ROLE = 'Manager';

// The one place the app asks "who is this and what may they do?".
//
// It used to be asked in two places that disagreed: this store checked
// `user.roles.includes('admin')` against an array of objects (never true), and
// taskStore ran its own five-name heuristic with three fallbacks. Both are gone
// — the backend now returns a single `company_role`, and every permission is
// enforced server-side regardless of what the UI decides to show.
export const useAuthStore = defineStore('authStore', {
  state: () => ({
    token: localStorage.getItem('token') || '',
    user: JSON.parse(localStorage.getItem('user')) || null,
    // Permission codes the current user holds company-wide, from /profile.
    // Used only to decide what to SHOW; the API enforces independently.
    permissions: JSON.parse(localStorage.getItem('permissions')) || []
  }),
  getters: {
    isAuthenticated: (state) => Boolean(state.token),
    companyRole: (state) => state.user?.company_role || null,
    roleName: (state) => state.user?.company_role?.name || '',
    isAdmin: (state) => state.user?.company_role?.name === ADMIN_ROLE,
    // Admin bypasses the permission table on the server, so it must bypass here
    // too or the UI would hide things the user can actually do.
    can: (state) => (code) =>
      state.user?.company_role?.name === ADMIN_ROLE ||
      (state.permissions || []).includes(code),

    // Whether the user holds `code` ON A GIVEN PROJECT.
    //
    // `can` above only knows the company-wide role, so it answers "no" for
    // someone who is a plain User company-wide but TL or Manager on one
    // project — exactly the case the per-project override exists for. This
    // mirrors get_effective_role on the server: a project membership's role
    // wins, and the company role is the fallback.
    //
    // `membership` is the caller's ProjectUser row (or undefined) and `roles`
    // the loaded role catalogue — passed in rather than read from here, since
    // both live in taskStore alongside the open project.
    canIn: (state) => (code, membership, roles = []) => {
      if (state.user?.company_role?.name === ADMIN_ROLE) return true;

      const projectRoleName = membership?.role;
      if (projectRoleName) {
        if (projectRoleName === ADMIN_ROLE) return true;
        const role = roles.find((r) => r.name === projectRoleName);
        // A role not loaded yet grants nothing, rather than falling back to the
        // company role and showing more than the project allows.
        if (!role) return false;
        return (role.permissions || []).some((p) => (p.name || p.code || p) === code);
      }
      return (state.permissions || []).includes(code);
    }
  },
  actions: {
    setAuthData(token, userData, permissions) {
      this.token = token;
      this.user = userData;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      if (permissions !== undefined) this.setPermissions(permissions);
    },
    setPermissions(permissions) {
      this.permissions = Array.isArray(permissions) ? permissions : [];
      localStorage.setItem('permissions', JSON.stringify(this.permissions));
    },
    setToken(token) {
      this.token = token;
      localStorage.setItem('token', token);
    },
    clearToken() {
      this.token = '';
      this.user = null;
      this.permissions = [];
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('permissions');
    }
  }
});
